import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { IOrderSearch } from '../../../../../interfaces/requests.interface';

export type BatteryLevel = 'low' | 'medium' | 'high' | 'all';
export type ListedStatus = 'listed' | 'not-listed' | 'all';
export type ExportFormat = 'csv' | 'xlsx';

export const getBatteryLevel = (percentage: number): BatteryLevel => {
    if (percentage <= 20) return 'low';
    if (percentage <= 50) return 'medium';
    return 'high';
};

export const getBatteryClass = (percentage: number) => {
    if (percentage <= 20) return 'var(--color-error)';
    if (percentage <= 50) return 'var(--color-yellow-2)';
    return 'var(--color-green-4)';
};

export const groupDriversByRound = (drivers: IOrderSearch[]) => {
    const groups: Record<number, IOrderSearch[]> = {};
    drivers.forEach((item) => {
        if (!groups[item.round]) {
            groups[item.round] = [];
        }
        groups[item.round].push(item);
    });
    return Object.entries(groups)
        .map(([round, driversList]) => ({ round: Number(round), drivers: driversList }))
        .sort((a, b) => a.round - b.round);
};

interface ExportLabels {
    name: string;
    email: string;
    round: string;
    listed: string;
    status: string;
    notified: string;
    notifiedAt: string;
    received: string;
    receivedAt: string;
    read: string;
    readAt: string;
    accepted: string;
    acceptedAt: string;
    battery: string;
    rejectedReason: string;
    yes: string;
    no: string;
}

const getDriverStatus = (metadata: IOrderSearch['metadata'], labels: ExportLabels): string => {
    if (metadata.wasAccepted) return labels.accepted;
    if (metadata.wasRead) return labels.read;
    if (metadata.wasReceived) return labels.received;
    if (metadata.wasNotified) return labels.notified;
    return '-';
};

const formatTime = (dateStr?: string): string => (
    dateStr ? dayjs(dateStr).format('HH:mm:ss') : '-'
);

type TranslateFn = (key: string) => string;

const buildExportRows = (
    drivers: IOrderSearch[],
    labels: ExportLabels,
    translateReason: TranslateFn,
) => drivers.map((driver) => ({
    [labels.name]: driver.driverName,
    [labels.email]: driver.driverEmail,
    [labels.round]: driver.round,
    [labels.listed]: driver.metadata.wasListed ? labels.yes : labels.no,
    [labels.status]: getDriverStatus(driver.metadata, labels),
    [labels.notified]: driver.metadata.wasNotified ? labels.yes : labels.no,
    [labels.notifiedAt]: formatTime(driver.metadata.wasNotifiedAt),
    [labels.received]: driver.metadata.wasReceived ? labels.yes : labels.no,
    [labels.receivedAt]: formatTime(driver.metadata.wasReceivedAt),
    [labels.read]: driver.metadata.wasRead ? labels.yes : labels.no,
    [labels.readAt]: formatTime(driver.metadata.wasReadAt),
    [labels.accepted]: driver.metadata.wasAccepted ? labels.yes : labels.no,
    [labels.acceptedAt]: formatTime(driver.metadata.wasAcceptedAt),
    [labels.battery]: `${driver.metadata.batteryPercentage}%`,
    [labels.rejectedReason]: driver.metadata.rejectedReason
        ? translateReason(driver.metadata.rejectedReason.toUpperCase())
        : '-',
}));

export const exportDriverSearch = (
    drivers: IOrderSearch[],
    format: ExportFormat,
    bookingNumericId: number,
    labels: ExportLabels,
    translateReason: TranslateFn,
) => {
    const rows = buildExportRows(drivers, labels, translateReason);
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Ajustar anchos de columna
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
        wch: Math.max(key.length, 15),
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Driver Search');

    const fileName = `busqueda-conductores-${bookingNumericId}`;

    if (format === 'csv') {
        XLSX.writeFile(workbook, `${fileName}.csv`, { bookType: 'csv' });
    } else {
        XLSX.writeFile(workbook, `${fileName}.xlsx`, { bookType: 'xlsx' });
    }
};

export const getReasonIcon = (rejectedReason?: string) => {
    if (!rejectedReason) return 'close-circle';

    switch (rejectedReason.toUpperCase()) {
    case 'BATTERY':
        return 'thunderbolt';
    case 'WALLET':
        return 'wallet';
    case 'WORK_BALANCE':
    case 'WORKBALANCE':
        return 'dollar';
    default:
        return 'close-circle';
    }
};
